import {
  CollectionLockup,
  CollectionLockupContent,
  CollectionLockupTitle,
} from "@opensea/app-kit"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import type { TextBodyProps } from "@opensea/ui-kit"
import {
  Badge,
  Flex,
  ItemAvatar,
  ItemContent,
  ItemDescription,
  TableCell,
  TableRow,
  TextBody,
  TextOverflowTooltip,
  Tooltip,
} from "@opensea/ui-kit"
import { ErrorFilled } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { Item } from "../Item"
import { QUOTE_RECEIPT_TABLE_COLUMN_CLASSNAMES } from "../QuoteReceipt/constants"
import { CollectionSweepQuoteReceiptFragment } from "./CollectionSweepQuoteReceipt.fragment"
import { CollectionSweepQuoteReceiptRange } from "./CollectionSweepQuoteReceiptRange"
import { getCollectionSweepQuoteError } from "./getCollectionSweepQuoteError"

type Props = {
  maxAmountOfItems: number
  quote: FragmentOf<typeof CollectionSweepQuoteReceiptFragment>
}

export const CollectionSweepQuoteReceiptSummary = ({
  maxAmountOfItems,
  quote: quoteKey,
}: Props) => {
  const t = useTranslations("wallet.CollectionSweepQuoteReceiptSummary")
  const quote = readFragment(CollectionSweepQuoteReceiptFragment, quoteKey)
  const collection = quote.collection
  const error = getCollectionSweepQuoteError(quote.quote)

  const descriptionProps = {
    color: "text-secondary",
    children: t("maxAmountOfItems", { count: maxAmountOfItems }),
  } as const satisfies TextBodyProps

  return (
    <div>
      <TableRow>
        <TableCell className={QUOTE_RECEIPT_TABLE_COLUMN_CLASSNAMES.item}>
          <Item key={collection.name} variant="unstyled">
            <ItemAvatar
              className="rounded"
              size={48}
              src={collection.imageUrl}
            />

            <ItemContent>
              <CollectionLockup collection={quote.collection}>
                <CollectionLockupContent>
                  <CollectionLockupTitle
                    color="text-secondary"
                    weight="regular"
                  />
                </CollectionLockupContent>
              </CollectionLockup>
              {error ? (
                <ItemDescription className="mt-1 whitespace-nowrap">
                  <Tooltip align="start" content={t("quoteNotFound")}>
                    <Badge icon={ErrorFilled} variant="error">
                      {"Unavailable"}
                    </Badge>
                  </Tooltip>
                </ItemDescription>
              ) : (
                <Flex className="items-center gap-2">
                  <TextOverflowTooltip>
                    <TextBody {...descriptionProps} />
                  </TextOverflowTooltip>
                </Flex>
              )}
            </ItemContent>
          </Item>
        </TableCell>

        {error ? null : (
          <TableCell className={QUOTE_RECEIPT_TABLE_COLUMN_CLASSNAMES.price}>
            <CollectionSweepQuoteReceiptRange
              maxAmountOfItems={maxAmountOfItems}
              quote={quote}
            />
          </TableCell>
        )}
      </TableRow>

      <Flex className="w-full flex-row">
        <TextBody color="text-secondary">{t("substitution")}</TextBody>
      </Flex>
    </div>
  )
}
